// components/copilot/export/pptxExport.js
import { fmtTime } from "../utils/format";
import { isImageAttachment } from "../utils/attachments";

const PPTX_LAYOUT = "LAYOUT_WIDE"; // 13.33 x 7.5
const SLIDE_W = 13.33;
const SLIDE_H = 7.5;

// ─── Design tokens ───────────────────────────────────────────────────────────
const C = {
  dark:       "111827",
  accent:     "FFCC33",
  white:      "FFFFFF",
  bg:         "F9FAFB",
  bgDark:     "0B1220",
  border:     "E5E7EB",
  muted:      "6B7280",
  userBubble: "1E293B",
  asstBubble: "FFFFFF",
  tableHead:  "1E293B",
  tableHeadTx:"FFFFFF",
  tableRow1:  "F8FAFC",
  tableRow2:  "FFFFFF",
  tableBorder:"CBD5E1",
  codeBlock:  "0F172A",
  codeBorder: "FFCC33",
  codeTx:     "E2E8F0",
  quoteBg:    "FFF8E1",
  quoteBorder:"FFCC33",
};

// Put file here: public/vendor/pptxgen.bundle.js
async function loadPptxGen() {
  if (window.PptxGenJS) return window.PptxGenJS;
  const base = (typeof process !== "undefined" && process.env?.PUBLIC_URL) || "";
  const src = `${base}/vendor/pptxgen.bundle.js`;
  await new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error("Failed to load pptxgen bundle."));
    document.body.appendChild(script);
  });
  if (!window.PptxGenJS) throw new Error("pptxgen bundle loaded but PptxGenJS not found on window.");
  return window.PptxGenJS;
}

function safeFileBase(name) {
  return String(name || "mad_pilot_export").trim().replace(/[^\w\-]+/g, "_").replace(/_+/g, "_").slice(0, 80);
}

function blobToDataUri(blob) {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Failed to read image blob."));
      reader.readAsDataURL(blob);
    } catch (e) { reject(e); }
  });
}

async function dataUriToImageSize(dataUri) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
    img.onerror = () => resolve({ w: 1, h: 1 });
    img.src = dataUri;
  });
}

function containRect(nw, nh, maxW, maxH) {
  const iw = Math.max(1, Number(nw) || 1);
  const ih = Math.max(1, Number(nh) || 1);
  const scale = Math.min(maxW / iw, maxH / ih);
  const w = iw * scale, h = ih * scale;
  return { x: (maxW - w) / 2, y: (maxH - h) / 2, w, h };
}

// ─── Text measurement helpers ─────────────────────────────────────────────────

function getMeasureCtx() {
  const c = document.createElement("canvas");
  return c.getContext("2d");
}
function inToPx(inches) { return (Number(inches) || 0) * 96; }
function ptToPx(pt) { return (Number(pt) || 0) * (96 / 72); }
function makeFontCSS(fontFace, fontSizePt, bold) {
  return `${bold ? 700 : 400} ${Math.max(8, ptToPx(fontSizePt))}px ${fontFace}`;
}
function lineHeightIn(fontSizePt, ls = 1.18) { return ((Number(fontSizePt) || 12) * ls) / 72; }
function estimateHeightIn(lines, fontSizePt, ls = 1.18) {
  return Math.max(lineHeightIn(fontSizePt, ls), lines * lineHeightIn(fontSizePt, ls));
}

function wrapTextToLines(text, maxWidthIn, fontFace, fontSizePt, bold = false) {
  const ctx = getMeasureCtx();
  if (!ctx) return String(text || "").replace(/\r\n/g, "\n").split("\n");
  ctx.font = makeFontCSS(fontFace, fontSizePt, bold);
  const maxPx = inToPx(maxWidthIn);
  const raw = String(text || "").replace(/\r\n/g, "\n");
  const lines = [];
  for (const para of raw.split("\n")) {
    if (!para.trim()) { lines.push(""); continue; }
    let cur = "";
    for (const w of para.split(/\s+/).filter(Boolean)) {
      const next = cur ? `${cur} ${w}` : w;
      if (ctx.measureText(next).width <= maxPx) cur = next;
      else { if (cur) lines.push(cur); cur = w; }
    }
    if (cur) lines.push(cur);
  }
  return lines;
}

// ─── Markdown tokenizer ───────────────────────────────────────────────────────

function parseMdTableToRows(text) {
  // Returns { headers: string[], rows: string[][] } or null
  const lines = text.trim().split("\n").map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return null;

  const parseRow = (line) =>
    line.replace(/^\||\|$/g, "").split("|").map(c => c.trim());

  const isSep = (line) => /^\|?[\s:|-]+\|/.test(line);

  // Find separator line
  let sepIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (isSep(lines[i])) { sepIdx = i; break; }
  }
  if (sepIdx < 1) return null;

  const headers = parseRow(lines[0]);
  const rows = [];
  for (let i = sepIdx + 1; i < lines.length; i++) {
    if (lines[i].includes("|")) rows.push(parseRow(lines[i]));
  }
  return { headers, rows };
}

function looksLikeMdTable(lines) {
  const l = (lines || []).filter(x => String(x || "").trim());
  if (l.length < 2) return false;
  const head = l.slice(0, 3).map(x => x.trim());
  const hasPipe = head.some(x => x.startsWith("|"));
  const hasSep = head.some(x => /^\|\s*:?-{2,}/.test(x) || x.includes("---|") || x.includes("|---"));
  return hasPipe && hasSep;
}

function tokenizeMarkdown(md) {
  const src = String(md || "");
  if (!src.trim()) return [];
  const out = [];
  const re = /```([\s\S]*?)```/g;
  let last = 0, m;
  while ((m = re.exec(src))) {
    const before = src.slice(last, m.index);
    if (before.trim()) out.push(...tokenizeNonCode(before));
    out.push({ type: "code", text: (m[1] || "").replace(/^\n+|\n+$/g, "") });
    last = m.index + m[0].length;
  }
  const tail = src.slice(last);
  if (tail.trim()) out.push(...tokenizeNonCode(tail));
  return out;
}

function tokenizeNonCode(text) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let buf = [], mode = null;

  const flush = () => {
    if (!buf.length) return;
    const joined = buf.join("\n").trimEnd();
    if (mode === "list")  out.push({ type: "list", items: buf.slice() });
    else if (mode === "quote") out.push({ type: "quote", text: joined.replace(/^>\s?/gm, "") });
    else if (mode === "table") out.push({ type: "table", text: joined });
    else out.push({ type: "para", text: joined });
    buf = []; mode = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const l = lines[i] || "";
    const hm = l.match(/^(#{1,3})\s+(.*)$/);
    if (hm) { flush(); out.push({ type: "heading", level: hm[1].length, text: hm[2] || "" }); continue; }
    if (/^\s*>\s?/.test(l)) { if (mode && mode !== "quote") flush(); mode = "quote"; buf.push(l); continue; }
    if (/^\s*([-*]|\d+\.)\s+/.test(l)) {
      if (mode && mode !== "list") flush();
      mode = "list";
      buf.push(l.replace(/^\s*([-*]|\d+\.)\s+/, ""));
      continue;
    }
    if (l.trim().startsWith("|")) {
      if (mode !== "table") {
        const peek = [l, lines[i + 1] || "", lines[i + 2] || ""];
        if (looksLikeMdTable(peek)) { if (mode) flush(); mode = "table"; buf.push(l); continue; }
      } else { buf.push(l); continue; }
    }
    if (!l.trim()) { flush(); continue; }
    if (mode) flush();
    buf.push(l);
  }
  flush();
  return out;
}

// ─── Inline markdown bold/italic renderer → rich text array ──────────────────

function parseInlineRich(text, baseOpts = {}) {
  // Returns pptxgenjs rich text array respecting **bold** and *italic*
  const segments = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/g;
  let last = 0, m;
  while ((m = re.exec(text))) {
    if (m.index > last) segments.push({ text: text.slice(last, m.index), options: { ...baseOpts } });
    if (m[2] != null) segments.push({ text: m[2], options: { ...baseOpts, bold: true } });
    else if (m[3] != null) segments.push({ text: m[3], options: { ...baseOpts, italic: true } });
    else if (m[4] != null) segments.push({ text: m[4], options: { ...baseOpts, fontFace: "Consolas", fontSize: (baseOpts.fontSize || 14) - 1 } });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ text: text.slice(last), options: { ...baseOpts } });
  return segments.length ? segments : [{ text, options: { ...baseOpts } }];
}

// Strip inline markdown for plain string use (table cells etc)
function stripInline(text) {
  return String(text || "").replace(/\*\*([^*]+)\*\*/g, "$1").replace(/\*([^*]+)\*/g, "$1").replace(/`([^`]+)`/g, "$1");
}

// ─── Slide builders ───────────────────────────────────────────────────────────

function addCoverSlide(pptx, { title, metaLines = [] }) {
  const slide = pptx.addSlide();
  slide.background = { color: C.dark };

  // Gold accent bar left edge
  slide.addShape("rect", { x: 0, y: 0, w: 0.18, h: SLIDE_H, fill: { color: C.accent }, line: { color: C.accent } });

  // Title
  slide.addText(title || "MAD Pilot Export", {
    x: 0.55, y: 2.4, w: SLIDE_W - 1.1, h: 0.9,
    fontFace: "Calibri", fontSize: 36, bold: true, color: C.accent,
    align: "left", valign: "mid",
  });

  // Divider
  slide.addShape("rect", { x: 0.55, y: 3.42, w: 5.5, h: 0.04, fill: { color: C.accent }, line: { color: C.accent } });

  // Meta
  if (metaLines?.length) {
    slide.addText(metaLines.join("\n"), {
      x: 0.55, y: 3.6, w: SLIDE_W - 1.1, h: 2.5,
      fontFace: "Calibri", fontSize: 13, color: "9CA3AF",
      valign: "top", lineSpacingMultiple: 1.5,
    });
  }
}

// ─── Chat-like slide renderer ─────────────────────────────────────────────────

function addChatLikeSlides(pptx, { header, text, isUser }) {
  const fontFace = "Calibri";
  const monoFace = "Consolas";
  const blocks = tokenizeMarkdown(text);

  // Layout constants
  const pageMargin = 0.6;
  const bubbleW = Math.min(SLIDE_W - pageMargin * 2, SLIDE_W * 0.78);
  const bubbleX = isUser ? SLIDE_W - pageMargin - bubbleW : pageMargin;
  const bubbleTop = 0.72;
  const bubbleBottom = 0.5;
  const bubbleH = SLIDE_H - bubbleTop - bubbleBottom;

  const padX = 0.4;
  const padY = 0.32;
  const contentX = bubbleX + padX;
  const contentW = bubbleW - padX * 2;
  const contentTop = bubbleTop + padY + 0.44; // below role chip + timestamp
  const contentBottom = bubbleTop + bubbleH - padY;

  const bubbleFill = isUser ? C.userBubble : C.asstBubble;
  const bubbleBorder = isUser ? C.accent : C.border;
  const textColor = isUser ? C.white : C.dark;
  const headerColor = isUser ? C.accent : C.muted;

  let slide = null;
  let cursorY = 0;

  const newSlide = (cont) => {
    slide = pptx.addSlide();
    slide.background = { color: C.bg };

    // Subtle side-tint behind the non-active side
    slide.addShape("rect", {
      x: isUser ? 0 : bubbleX + bubbleW,
      y: 0, w: isUser ? bubbleX : SLIDE_W - (bubbleX + bubbleW), h: SLIDE_H,
      fill: { color: "F1F5F9" }, line: { color: "F1F5F9" },
    });

    // Message bubble
    slide.addShape("rect", {
      x: bubbleX, y: bubbleTop, w: bubbleW, h: bubbleH,
      fill: { color: bubbleFill },
      line: { color: bubbleBorder, width: isUser ? 0 : 1 },
      shadow: isUser ? undefined : { type: "outer", color: "000000", blur: 12, offset: 2, angle: 135, opacity: 0.07 },
    });

    // Role chip
    const chipW = isUser ? 0.8 : 1.1;
    slide.addShape("rect", {
      x: contentX, y: bubbleTop + padY - 0.02, w: chipW, h: 0.24,
      fill: { color: isUser ? C.accent : "E5E7EB" }, line: { color: isUser ? C.accent : "E5E7EB" },
    });
    slide.addText(isUser ? "USER" : "ASSISTANT", {
      x: contentX, y: bubbleTop + padY - 0.02, w: chipW, h: 0.24,
      fontFace, fontSize: 8, bold: true, color: C.dark,
      align: "center", valign: "mid", margin: 0,
    });

    // Header / timestamp
    slide.addText(cont ? `${header} (cont.)` : header, {
      x: contentX + chipW + 0.14, y: bubbleTop + padY,
      w: contentW - chipW - 0.14, h: 0.28,
      fontFace, fontSize: 10, color: headerColor, valign: "mid", fit: "shrink",
    });

    cursorY = contentTop;
  };

  const spaceLeft = () => contentBottom - cursorY;

  const ensureSpace = (need) => {
    if (cursorY + need <= contentBottom) return;
    newSlide(true);
  };

  newSlide(false);

  const effectiveBlocks = blocks.length ? blocks : [{ type: "para", text: String(text || "") }];

  for (const b of effectiveBlocks) {
    if (!b) continue;

    // ── heading ──────────────────────────────────────────────────────────────
    if (b.type === "heading") {
      const lvl = b.level || 1;
      const fs = lvl === 1 ? 20 : lvl === 2 ? 17 : 15;
      const lines = wrapTextToLines(b.text, contentW, fontFace, fs, true);
      const h = estimateHeightIn(lines.length, fs, 1.15) + 0.1;
      ensureSpace(h);
      slide.addText(b.text, {
        x: contentX, y: cursorY, w: contentW, h,
        fontFace, fontSize: fs, bold: true, color: textColor,
        lineSpacingMultiple: 1.15, valign: "top", fit: "shrink",
      });
      cursorY += h + 0.14;
      continue;
    }

    // ── list ─────────────────────────────────────────────────────────────────
    if (b.type === "list") {
      const fs = 13;
      const richItems = [];
      for (let i = 0; i < (b.items || []).length; i++) {
        const item = b.items[i];
        const inlineSegs = parseInlineRich(item, { fontSize: fs, color: textColor, fontFace, breakLine: i < b.items.length - 1 });
        richItems.push(
          { text: "• ", options: { fontSize: fs, color: isUser ? C.accent : "64748B", fontFace, bold: true } },
          ...inlineSegs.map((s, si) => ({
            ...s,
            options: {
              ...s.options,
              breakLine: si === inlineSegs.length - 1 ? (i < b.items.length - 1) : false,
            },
          }))
        );
      }
      const lineCount = b.items.reduce((acc, item) => {
        return acc + wrapTextToLines(item, contentW - 0.3, fontFace, fs).length;
      }, 0);
      const h = estimateHeightIn(lineCount || 1, fs, 1.25) + 0.1;
      ensureSpace(h);
      slide.addText(richItems, {
        x: contentX, y: cursorY, w: contentW, h,
        fontFace, fontSize: fs, color: textColor,
        lineSpacingMultiple: 1.25, valign: "top", fit: "shrink",
      });
      cursorY += h + 0.16;
      continue;
    }

    // ── blockquote ────────────────────────────────────────────────────────────
    if (b.type === "quote") {
      const fs = 13;
      const lines = wrapTextToLines(b.text, contentW - 0.6, fontFace, fs);
      const h = estimateHeightIn(lines.length || 1, fs, 1.22) + 0.32;
      ensureSpace(h);
      slide.addShape("rect", {
        x: contentX, y: cursorY, w: 0.06, h,
        fill: { color: C.accent }, line: { color: C.accent },
      });
      slide.addShape("rect", {
        x: contentX + 0.06, y: cursorY, w: contentW - 0.06, h,
        fill: { color: isUser ? "1C2A3E" : C.quoteBg }, line: { color: isUser ? "1C2A3E" : C.quoteBg },
      });
      slide.addText(b.text, {
        x: contentX + 0.22, y: cursorY + 0.12, w: contentW - 0.44, h: h - 0.18,
        fontFace, fontSize: fs, italic: true,
        color: isUser ? "CBD5E1" : "374151",
        lineSpacingMultiple: 1.22, valign: "top", fit: "shrink",
      });
      cursorY += h + 0.16;
      continue;
    }

    // ── TABLE — real pptxgenjs table ─────────────────────────────────────────
    if (b.type === "table") {
      const parsed = parseMdTableToRows(b.text);
      if (!parsed) {
        // Fallback: render as code block
        b.type = "code";
      } else {
        const { headers, rows } = parsed;
        const cols = headers.length || 1;

        // Estimate row height based on content
        const cellFs = 10;
        const colW = contentW / cols;
        const estimateCellLines = (txt) =>
          Math.max(1, wrapTextToLines(stripInline(txt), colW - 0.12, fontFace, cellFs).length);

        const headerLineCount = Math.max(...headers.map(estimateCellLines));
        const headerRowH = estimateHeightIn(headerLineCount, cellFs, 1.3) + 0.14;

        const dataRowHeights = rows.map(row =>
          Math.max(...row.map(estimateCellLines)) * lineHeightIn(cellFs, 1.3) + 0.14
        );
        const totalTableH = headerRowH + dataRowHeights.reduce((a, h) => a + h, 0);

        // If table won't fit at all on any slide, split by rows
        const maxTableH = spaceLeft();
        const fitsInOne = totalTableH <= maxTableH;

        const renderTableChunk = (hdrs, chunkRows, atY) => {
          // Build pptxgenjs table data
          const headerCells = hdrs.map(h => ({
            text: stripInline(h),
            options: {
              bold: true,
              fontSize: cellFs,
              color: C.tableHeadTx,
              fontFace,
              fill: { color: C.tableHead },
              align: "left",
              valign: "mid",
              margin: [4, 6, 4, 6],
            },
          }));

          const dataCells = chunkRows.map((row, ri) =>
            row.map(cell => ({
              text: stripInline(cell),
              options: {
                fontSize: cellFs,
                color: C.dark,
                fontFace,
                fill: { color: ri % 2 === 0 ? C.tableRow1 : C.tableRow2 },
                align: "left",
                valign: "mid",
                margin: [3, 6, 3, 6],
              },
            }))
          );

          const tableData = [headerCells, ...dataCells];
          const chunkH = headerRowH + chunkRows.reduce((a, _, i) => a + dataRowHeights[i], 0);

          slide.addTable(tableData, {
            x: contentX,
            y: atY,
            w: contentW,
            h: Math.min(chunkH, contentBottom - atY),
            colW: Array(cols).fill(colW),
            border: { pt: 0.5, color: C.tableBorder },
          });

          return chunkH;
        };

        if (fitsInOne) {
          ensureSpace(totalTableH + 0.1);
          renderTableChunk(headers, rows, cursorY);
          cursorY += totalTableH + 0.18;
        } else {
          // Split rows across slides
          let rowIdx = 0;
          let firstChunk = true;
          while (rowIdx <= rows.length) {
            if (!firstChunk) newSlide(true);
            firstChunk = false;

            const available = spaceLeft() - 0.05;
            let chunkH = headerRowH;
            let end = rowIdx;
            while (end < rows.length && chunkH + dataRowHeights[end] <= available) {
              chunkH += dataRowHeights[end];
              end++;
            }
            if (end === rowIdx) end = Math.min(rowIdx + 1, rows.length); // at least 1 row

            const chunkRows = rows.slice(rowIdx, end);
            renderTableChunk(headers, chunkRows, cursorY);
            cursorY += chunkH + 0.18;
            rowIdx = end;
            if (rowIdx >= rows.length) break;
          }
        }
        continue;
      }
    }

    // ── code block ────────────────────────────────────────────────────────────
    if (b.type === "code") {
      const fs = 11;
      const code = String(b.text || "").replace(/\t/g, "  ");
      const lines = wrapTextToLines(code, contentW - 0.5, monoFace, fs);

      let idx = 0;
      while (idx < lines.length) {
        const available = spaceLeft();
        const maxLines = Math.max(1, Math.floor((available - 0.32) / lineHeightIn(fs, 1.12)));
        const slice = lines.slice(idx, idx + maxLines);
        const boxH = estimateHeightIn(slice.length || 1, fs, 1.12) + 0.32;
        ensureSpace(boxH);

        slide.addShape("rect", {
          x: contentX, y: cursorY, w: contentW, h: boxH,
          fill: { color: C.codeBlock }, line: { color: C.codeBorder, width: 1 },
        });
        // Language label dot
        slide.addShape("rect", {
          x: contentX + contentW - 0.22, y: cursorY + 0.06, w: 0.14, h: 0.14,
          fill: { color: C.accent }, line: { color: C.accent },
        });
        slide.addText(slice.join("\n") || " ", {
          x: contentX + 0.18, y: cursorY + 0.14, w: contentW - 0.36, h: boxH - 0.22,
          fontFace: monoFace, fontSize: fs, color: C.codeTx,
          lineSpacingMultiple: 1.12, valign: "top", fit: "shrink",
        });
        cursorY += boxH + 0.16;
        idx += maxLines;
        if (idx < lines.length && spaceLeft() < 0.5) newSlide(true);
      }
      continue;
    }

    // ── paragraph (default) ───────────────────────────────────────────────────
    {
      const fs = 13;
      const richSegs = parseInlineRich(b.text, { fontSize: fs, color: textColor, fontFace });
      const lines = wrapTextToLines(b.text, contentW, fontFace, fs);

      let i = 0;
      while (i < lines.length) {
        const available = spaceLeft();
        const maxLines = Math.max(1, Math.floor(available / lineHeightIn(fs, 1.25)));
        const slice = lines.slice(i, i + maxLines);
        const boxH = estimateHeightIn(slice.length || 1, fs, 1.25) + 0.06;
        ensureSpace(boxH);

        // Use rich text for first chunk (handles bold/italic), plain for continuations
        if (i === 0 && richSegs.length > 1) {
          slide.addText(richSegs, {
            x: contentX, y: cursorY, w: contentW, h: boxH,
            fontFace, fontSize: fs, color: textColor,
            lineSpacingMultiple: 1.25, valign: "top", fit: "shrink",
          });
        } else {
          slide.addText(slice.join("\n") || "—", {
            x: contentX, y: cursorY, w: contentW, h: boxH,
            fontFace, fontSize: fs, color: textColor,
            lineSpacingMultiple: 1.25, valign: "top", fit: "shrink",
          });
        }

        cursorY += boxH + 0.14;
        i += maxLines;
        if (i < lines.length && spaceLeft() < 0.4) newSlide(true);
      }
    }
  }
}

function addTextSlides(pptx, { header, text, isUser = false }) {
  try {
    addChatLikeSlides(pptx, { header, text, isUser });
  } catch (err) {
    console.warn("addChatLikeSlides failed, falling back:", err);
    const slide = pptx.addSlide();
    slide.background = { color: C.bg };
    slide.addText(header, { x: 0.7, y: 0.5, w: SLIDE_W - 1.4, h: 0.5, fontSize: 16, bold: true, color: C.dark });
    slide.addText(String(text || "—"), {
      x: 0.7, y: 1.15, w: SLIDE_W - 1.4, h: SLIDE_H - 1.75,
      fontSize: 13, color: C.dark, valign: "top", lineSpacingMultiple: 1.2, fit: "shrink",
    });
  }
}

async function addImageSlide(pptx, { dataUri, caption }) {
  const slide = pptx.addSlide();
  slide.background = { color: C.dark };

  slide.addShape("rect", { x: 0, y: 0, w: 0.18, h: SLIDE_H, fill: { color: C.accent }, line: { color: C.accent } });

  slide.addText(caption || "Image", {
    x: 0.4, y: 0.14, w: SLIDE_W - 0.8, h: 0.38,
    fontFace: "Calibri", fontSize: 13, bold: true, color: C.accent, fit: "shrink",
  });

  const marginX = 0.6;
  const topY = 0.72;
  const maxW = SLIDE_W - marginX * 2;
  const maxH = SLIDE_H - topY - 0.4;
  const size = await dataUriToImageSize(dataUri);
  const rect = containRect(size.w, size.h, maxW, maxH);
  slide.addImage({ data: dataUri, x: marginX + rect.x, y: topY + rect.y, w: rect.w, h: rect.h });
}

async function writePptxAndDownload(pptx, filenameBase) {
  const blob = await pptx.write("blob");
  const url = URL.createObjectURL(blob);
  const fileName = `${safeFileBase(filenameBase)}.pptx`;
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }
}

// ─── Public exports ───────────────────────────────────────────────────────────

export async function exportMessageToPpt({ activeSessionId, auditId, msg, downloadAttachmentBlob }) {
  const PptxGenJS = await loadPptxGen();
  const role = msg?.role || "assistant";
  const text = String(msg?.text || "").trim();
  const errorText = String(msg?.error_text || "").trim();
  const isErr = (msg?.status || "") === "error" || !!errorText;
  const payload = isErr ? `Error: ${errorText || "Unknown error"}${text ? `\n\n${text}` : ""}` : text;
  const attachments = Array.isArray(msg?.attachments) ? msg.attachments : [];
  const imageAttachments = attachments.filter(isImageAttachment);

  const pptx = new PptxGenJS();
  pptx.layout = PPTX_LAYOUT;

  addCoverSlide(pptx, {
    title: "MAD Pilot — Message Export",
    metaLines: [
      activeSessionId != null ? `Session  #${activeSessionId}` : null,
      msg?.id != null ? `Message  #${msg.id}` : null,
      role ? `Role  ${role}` : null,
      msg?.created_at ? `Time  ${fmtTime(msg.created_at)}` : null,
      msg?.model ? `Model  ${msg.model}` : null,
      auditId ? `Audit  #${auditId}` : null,
    ].filter(Boolean),
  });

  addTextSlides(pptx, {
    header: `${String(role).toUpperCase()} • ${msg?.created_at ? fmtTime(msg.created_at) : ""}${msg?.model ? ` • ${msg.model}` : ""}`.trim(),
    text: payload || "—",
    isUser: role === "user",
  });

  for (const a of imageAttachments) {
    const id = a?.id;
    if (!id) continue;
    try {
      const blob = await downloadAttachmentBlob(id);
      const dataUri = await blobToDataUri(blob);
      await addImageSlide(pptx, { dataUri, caption: a?.filename || `Image #${id}` });
    } catch {
      addTextSlides(pptx, { header: "ATTACHMENT", text: `Failed to include image attachment #${id}.`, isUser: false });
    }
  }

  await writePptxAndDownload(pptx, `session_${activeSessionId ?? "na"}_msg_${msg?.id ?? "na"}`);
}

export async function exportSessionToPpt({ activeSessionId, auditId, sessionPayload, downloadAttachmentBlob }) {
  const all = Array.isArray(sessionPayload?.messages) ? sessionPayload.messages : [];
  if (!all.length) return;

  const PptxGenJS = await loadPptxGen();
  const pptx = new PptxGenJS();
  pptx.layout = PPTX_LAYOUT;

  addCoverSlide(pptx, {
    title: "MAD Pilot — Session Export",
    metaLines: [
      `Session  #${activeSessionId}`,
      sessionPayload?.session?.title ? `Title  ${sessionPayload.session.title}` : null,
      auditId ? `Audit  #${auditId}` : null,
      `Messages  ${all.length}`,
      `Exported  ${new Date().toLocaleString()}`,
    ].filter(Boolean),
  });

  for (const m of all) {
    const role = m?.role || "assistant";
    const text = String(m?.text || "").trim();
    const errorText = String(m?.error_text || "").trim();
    const isErr = (m?.status || "") === "error" || !!errorText;
    const payload = isErr ? `Error: ${errorText || "Unknown error"}${text ? `\n\n${text}` : ""}` : text || "—";

    addTextSlides(pptx, {
      header: `#${m?.id ?? "—"} • ${String(role).toUpperCase()} • ${m?.created_at ? fmtTime(m.created_at) : ""}${m?.model ? ` • ${m.model}` : ""}`.trim(),
      text: payload,
      isUser: role === "user",
    });

    const attachments = Array.isArray(m?.attachments) ? m.attachments : [];
    for (const a of attachments.filter(isImageAttachment)) {
      const id = a?.id;
      if (!id) continue;
      try {
        const blob = await downloadAttachmentBlob(id);
        const dataUri = await blobToDataUri(blob);
        await addImageSlide(pptx, { dataUri, caption: `#${m?.id ?? "—"} • ${a?.filename || `Image #${id}`}` });
      } catch {
        addTextSlides(pptx, { header: "ATTACHMENT", text: `Failed to include image attachment #${id} (message #${m?.id ?? "—"}).`, isUser: false });
      }
    }
  }

  await writePptxAndDownload(pptx, `session_${activeSessionId}_export`);
}